import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiGet } from '@/lib/api';
import { suggestSchool } from '@/lib/studentSyllabus';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const STATES = ['Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra',
  'Odisha', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'];

const OTHER = '__other__';
const selectCls = 'w-full mt-1 p-2 rounded-lg border border-slate-200 text-sm font-body bg-white';

/**
 * State → City → School, from the same lists onboarding uses.
 * "Other" is always available, and asks for the name exactly as the school
 * writes it — that is what keeps one school from becoming five.
 */
export function SchoolPicker({ value, onChange, board = '' }) {
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [filter, setFilter] = useState('');     // type-to-search over a long list
  const [adding, setAdding] = useState(false);
  const { state = '', city = '', school = '', school_other = '' } = value || {};

  useEffect(() => {
    if (!state) { setCities([]); return; }
    apiGet(`/onboarding/cities?state=${encodeURIComponent(state)}`)
      .then(d => setCities(d.cities || [])).catch(() => setCities([]));
  }, [state]);

  useEffect(() => {
    if (!state || !city) { setSchools([]); return; }
    apiGet(`/onboarding/schools?state=${encodeURIComponent(state)}&city=${encodeURIComponent(city)}&board=${encodeURIComponent(board)}`)
      .then(d => setSchools([...new Map((d.schools || []).map(s => [s.name.trim().toLowerCase(), s])).values()])).catch(() => setSchools([]));
  }, [state, city, board]);

  const set = (patch) => onChange({ ...(value || {}), ...patch });

  return (
    <div className="space-y-2.5" data-testid="school-picker">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div>
          <Label className="text-xs font-body text-slate-500">State</Label>
          <select data-testid="school-state-select" className={selectCls} value={state}
            onChange={e => set({ state: e.target.value, city: '', school: '', school_other: '' })}>
            <option value="">Select state</option>
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-body text-slate-500">City / Village</Label>
          <select data-testid="school-city-select" className={selectCls} value={city} disabled={!state}
            onChange={e => set({ city: e.target.value, school: '', school_other: '' })}>
            <option value="">{state ? 'Select city' : 'Pick a state first'}</option>
            {city && !cities.some(c => c.name === city) && <option value={city}>{city}</option>}
            {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label className="text-xs font-body text-slate-500">School</Label>
        {city && schools.length > 8 && (
          <Input data-testid="school-filter-input" value={filter} onChange={e => setFilter(e.target.value)}
            placeholder={`Search ${schools.length} schools in ${city}`} className="rounded-lg text-sm mt-1 mb-1.5" />
        )}
        <select data-testid="school-name-select" className={selectCls} value={school} disabled={!city}
          onChange={e => set({ school: e.target.value, school_other: '' })}>
          <option value="">{city ? 'Select school' : 'Pick a city first'}</option>
          {school && school !== OTHER && !schools.some(s => s.name === school) && <option value={school}>{school}</option>}
          {schools
            .filter(s => !filter.trim() || s.name.toLowerCase().includes(filter.trim().toLowerCase()))
            .map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          <option value={OTHER}>Other — my school isn't listed</option>
        </select>
      </div>

      {school === OTHER && (
        <div data-testid="school-other-block">
          <Input data-testid="school-other-input" value={school_other} placeholder="Your school's full name"
            onChange={e => set({ school_other: e.target.value })} className="rounded-lg text-sm" />
          <p className="text-[11px] text-slate-500 font-body mt-1.5 leading-relaxed">
            Please type the school name <strong>exactly as it appears on school documents</strong> — exam
            papers, notebooks or the school diary. It keeps us from creating duplicate schools and helps
            us serve you better.
          </p>
          <Button type="button" size="sm" variant="outline" disabled={adding || school_other.trim().length < 3}
            data-testid="school-other-submit" className="mt-2 rounded-lg"
            onClick={async () => {
              setAdding(true);
              try {
                const res = await suggestSchool({ name: school_other.trim(), state, city, board });
                // An admin confirms the spelling; the child uses it right away.
                set({ school: res.school, school_other: res.school });
                toast.success(res.message);
              } catch (e) { toast.error(e?.message || 'That name was not accepted'); }
              setAdding(false);
            }}>
            {adding ? 'Adding…' : 'Add my school to the list'}
          </Button>
        </div>
      )}
    </div>
  );
}

export function resolveSchoolName(value) {
  const { school = '', school_other = '' } = value || {};
  return school === OTHER ? school_other.trim() : school.trim();
}

export default SchoolPicker;
